import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import type { MaterialItem, StoreStats, MaterialComment, StatusFilter, CategoryFilter } from '../types';

// ─── Store Stats ────────────────────────────────────────────────────────────

export function useStoreStats() {
    return useQuery<StoreStats>({
        queryKey: ['admin', 'store', 'stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_store_stats');
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return normalized[0] as StoreStats;
        },
        staleTime: 30_000,
    });
}

// ─── Materials List ─────────────────────────────────────────────────────────

export function useStoreMaterials(statusFilter: StatusFilter, categoryFilter: CategoryFilter, search: string) {
    return useQuery<MaterialItem[]>({
        queryKey: ['admin', 'store', 'materials', statusFilter, categoryFilter, search],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 200 };
            if (statusFilter !== 'all') params.p_status_filter = statusFilter;
            if (categoryFilter !== 'all') params.p_category_filter = categoryFilter;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_get_materials', params);
            if (error) throw error;
            return normalizeRpcResponse(data) as MaterialItem[];
        },
        staleTime: 30_000,
    });
}

// ─── Material Comments ──────────────────────────────────────────────────────

export function useMaterialComments(materialId: string | null) {
    return useQuery<MaterialComment[]>({
        queryKey: ['admin', 'store', 'comments', materialId],
        queryFn: async () => {
            if (!materialId) return [];
            const { data, error } = await dbClient.rpc('admin_get_material_comments', {
                p_material_id: materialId,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as MaterialComment[];
        },
        enabled: !!materialId,
        staleTime: 30_000,
    });
}

// ─── Fishing Modalities ─────────────────────────────────────────────────────

export interface FishingModality {
    id: number;
    name: string;
}

export function useFishingModalities() {
    return useQuery<FishingModality[]>({
        queryKey: ['admin', 'fishing-modalities'],
        queryFn: async () => {
            // Strategy 1: Try RPC
            try {
                const { data, error } = await dbClient.rpc('get_fishing_modalities');
                if (!error && data) {
                    return normalizeRpcResponse(data) as FishingModality[];
                }
            } catch { /* RPC doesn't exist yet, fall through */ }

            // Strategy 2: Try direct table query
            try {
                const fallback = await dbClient
                    .from('fishing_modalities' as any)
                    .select('id, name')
                    .execute();
                if (!fallback.error && fallback.data && fallback.data.length > 0) {
                    return fallback.data as FishingModality[];
                }
            } catch { /* Table might not be accessible */ }

            // Strategy 3: Return empty array gracefully
            return [];
        },
        staleTime: 300_000, // 5 min
        retry: false, // Don't retry if both strategies fail
    });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useUpdateMaterialStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ materialId, newStatus, rejectionReason }: {
            materialId: string;
            newStatus: string;
            rejectionReason?: string;
        }) => {
            const { error } = await dbClient.rpc('admin_update_material_status', {
                p_material_id: materialId,
                p_new_status: newStatus,
                p_rejection_reason: rejectionReason || null,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store'] });
        },
    });
}

export function useDeleteMaterial() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (materialId: string) => {
            const { error } = await dbClient.rpc('admin_delete_material', {
                p_material_id: materialId,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store'] });
        },
    });
}

export function useUpdateMaterial() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { error } = await dbClient.rpc('admin_update_material', params);
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store'] });
        },
    });
}

export function useCreateMaterial() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_create_material', params);
            if (error) throw error;
            return data as { id: string; success: boolean } | null;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store'] });
        },
    });
}

export function useDeleteComment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (commentId: string) => {
            const { error } = await dbClient.rpc('admin_delete_material_comment', {
                p_comment_id: commentId,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store', 'comments'] });
            qc.invalidateQueries({ queryKey: ['admin', 'store', 'materials'] });
        },
    });
}

export function useExtractProduct() {
    return useMutation({
        mutationFn: async (url: string) => {
            const result = await apiClient.post<{
                title?: string;
                description?: string;
                images?: string[];
                price?: number;
                currency?: string;
                rating?: number;
                reviews_count?: number;
                error?: string;
            }>('/extract-product-data', { url });
            if (result.error) throw new Error(result.error.message);
            return result.data!;
        },
    });
}
